import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

// Run history reads the retained task records (sync_task_results). Its states are lowercase; the live status endpoint's
// are uppercase Celery states — they are different vocabularies and are not mixed.
export const HISTORY_STATUSES = ["submitted", "started", "success", "failure", "retry", "revoked"];
export const HISTORY_PAGE_SIZES = [25, 50, 100];
// Stored states that mean the run is still going (it can still change).
export const LIVE_HISTORY_STATUSES = ["submitted", "started", "retry"];
// How often a list that shows a running run refreshes itself (only while the tab is in focus).
export const LIVE_REFRESH_MS = 10000;
// How often the other pages (Overview, Schedules) refresh themselves while you are looking at them.
export const PAGE_REFRESH_MS = 30000;
// Options for a query that refreshes itself every PAGE_REFRESH_MS, and pauses while this window is not in focus.
export const PAGE_REFRESH = { pollingInterval: PAGE_REFRESH_MS, skipPollingIfUnfocused: true };
export const hasLiveRuns = (results) => (results || []).some((record) => LIVE_HISTORY_STATUSES.includes(record.status));
export const HISTORY_SORTS = [
  { value: "submitted_at", label: "Submitted" },
  { value: "completed_at", label: "Completed" },
  { value: "duration_seconds", label: "Duration" },
];

export const DEFAULT_HISTORY_FILTERS = {
  statuses: [],
  tasks: [],
  includeSystem: false,
  owner: "",
  queue: "",
  from: "",
  to: "",
  page: 1,
  size: 25,
  sortBy: "submitted_at",
  sortOrder: "desc",
};

const csv = (value) => (value ? value.split(",").map((part) => part.trim()).filter(Boolean) : []);

// The filters live in the address (h_*), next to ?tab=, so a filtered view can be shared or reloaded.
export const parseHistoryParams = (searchParams) => {
  const size = Number(searchParams.get("h_size"));
  const page = Number(searchParams.get("h_page"));
  const sortBy = searchParams.get("h_sort");
  return {
    statuses: csv(searchParams.get("h_status")).filter((status) => HISTORY_STATUSES.includes(status)),
    tasks: csv(searchParams.get("h_task")),
    includeSystem: searchParams.get("h_system") === "1",
    owner: searchParams.get("h_owner") || "",
    queue: searchParams.get("h_queue") || "",
    from: searchParams.get("h_from") || "",
    to: searchParams.get("h_to") || "",
    page: Number.isInteger(page) && page >= 1 ? page : 1,
    size: HISTORY_PAGE_SIZES.includes(size) ? size : DEFAULT_HISTORY_FILTERS.size,
    sortBy: HISTORY_SORTS.some((sort) => sort.value === sortBy) ? sortBy : DEFAULT_HISTORY_FILTERS.sortBy,
    sortOrder: searchParams.get("h_order") === "asc" ? "asc" : "desc",
  };
};

const SET = (params, key, value, empty) => {
  if (value === empty || value === "" || (Array.isArray(value) && !value.length)) params.delete(key);
  else params.set(key, Array.isArray(value) ? value.join(",") : String(value));
};

// Returns a new URLSearchParams with the history filters written in (defaults left out), everything else kept.
export const writeHistoryParams = (searchParams, filters) => {
  const params = new URLSearchParams(searchParams);
  SET(params, "h_status", filters.statuses);
  SET(params, "h_task", filters.tasks);
  SET(params, "h_system", filters.includeSystem ? "1" : "");
  SET(params, "h_owner", filters.owner.trim());
  SET(params, "h_queue", filters.queue.trim());
  SET(params, "h_from", filters.from);
  SET(params, "h_to", filters.to);
  SET(params, "h_page", filters.page, 1);
  SET(params, "h_size", filters.size, DEFAULT_HISTORY_FILTERS.size);
  SET(params, "h_sort", filters.sortBy, DEFAULT_HISTORY_FILTERS.sortBy);
  SET(params, "h_order", filters.sortOrder, DEFAULT_HISTORY_FILTERS.sortOrder);
  return params;
};

// The query for GET /sync/tasks/results. Dates are whole days in UTC (inclusive bounds on submitted_at).
export const toApiParams = (filters) => ({
  status: filters.statuses.join(","),
  task_name: filters.tasks.join(","),
  owner: filters.owner.trim(),
  queue: filters.queue.trim(),
  from_date: filters.from ? `${filters.from}T00:00:00Z` : undefined,
  to_date: filters.to ? `${filters.to}T23:59:59Z` : undefined,
  skip: (filters.page - 1) * filters.size,
  limit: filters.size,
  sort_by: filters.sortBy === DEFAULT_HISTORY_FILTERS.sortBy ? undefined : filters.sortBy,
  sort_order: filters.sortOrder === DEFAULT_HISTORY_FILTERS.sortOrder ? undefined : filters.sortOrder,
});

export const hasActiveFilters = (filters) =>
  Boolean(filters.statuses.length || filters.tasks.length || filters.owner.trim() || filters.queue.trim() || filters.from || filters.to);

// A stored record looked at the way a live task is, so the same outcome wording applies. "submitted" is still waiting
// for a worker, which the live states call PENDING.
export const historyStateOf = (status) => (status === "submitted" ? "PENDING" : String(status || "").toUpperCase());
export const recordAsTask = (record) => ({ taskName: record?.task_name, state: historyStateOf(record?.status), result: record?.result_summary ?? null, error: record?.error ?? null });

// The days of DATA a run was asked to process (not when it ran), from its task arguments or the requested scope:
// "2026-09-24", or "2026-09-18 → 2026-09-24". null when the run has no date range.
export const dataDays = (record) => {
  const sources = [record?.payload?.kwargs, record?.payload?.monitoring_scope];
  for (const source of sources) {
    const start = source?.start_date;
    if (start) {
      const end = source.end_date;
      return end && end !== start ? `${start} → ${end}` : String(start);
    }
  }
  return null;
};

export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) return "—";
  const value = Number(seconds);
  if (value < 1) return "<1 s";
  if (value < 60) return `${Math.round(value)} s`;
  const minutes = Math.floor(value / 60);
  const rest = Math.round(value % 60);
  if (minutes < 60) return rest ? `${minutes} min ${rest} s` : `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
};
